import { Select } from '@tigergraph/app-ui-lib/select';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Plus } from 'baseui/icon';
import { styled } from '@tigergraph/app-ui-lib/Theme';
import useHover from 'ahooks/lib/useHover';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Input } from '@tigergraph/app-ui-lib/input';
import { SelectOverrides } from 'baseui/select';
import { Pencil, Trash2 } from 'lucide-react';
import { providers, regions, workspaceSizes } from './priceConfig';
import { SpaceIcon } from '@/pages/home/icons';
import StorageIcon from '../icons/storage.svg?react';
import ResourcePlanJSON from './resource_price.json';
import { Pricing } from '@/pages/workgroup/type';
import { WorkspaceT, StorageT } from './type';
import { calculateStoragePrice, calculateWorkspacePrice } from '@/pages/misc/price/calculator';
import { getSaleMailLink } from '@/utils/utils';

const pricing = ResourcePlanJSON['Result'] as Pricing;

let workspaceID = 1;
let storageID = 1;

const hours = 12;
const days = 20;
const size = 100;

const typeOptions = [
  {
    id: 'write',
    label: 'Read-Write',
  },
  {
    id: 'read',
    label: 'Read-Only',
  },
];

const haOptions = [
  {
    id: 'Yes',
    label: 'Yes',
  },
  {
    id: 'No',
    label: 'No',
  },
];

const addOnsOptions = [
  {
    id: 'Insights',
    label: 'Insights',
  },
];

export default function Price() {
  const [css] = useStyletron();

  useEffect(() => {
    const onResize = () => {
      window.parent.postMessage(
        {
          type: 'resize',
          height: document.body.scrollHeight,
        },
        '*'
      );
    };

    onResize(); // Invoke the handler manually once initially
    const ob = new ResizeObserver((_) => onResize());
    ob.observe(document.body);
    return () => ob.unobserve(document.body);
  }, []);

  const [cloudProvider, setCloudProvider] = useState('AWS');
  const { platform, cloud_provider } = providers.find((p) => p.id === cloudProvider)!;
  console.log({
    platform,
    cloud_provider,
  });
  const [region, setRegion] = useState('us-east-1');
  const [workspaces, setWorkspaces] = useState<WorkspaceT[]>(() => {
    const workspace: WorkspaceT = {
      id: workspaceID,
      name: `Workspace ${workspaceID}`,
      is_rw: true,
      enable_ha: false,
      enable_insights: false,
      workspace_type: workspaceSizes[0].typeName,
      hours: hours,
      days: days,
      canDelete: false,
    };
    return [workspace];
  });
  const [storages, setStorages] = useState<StorageT[]>(() => {
    const storage: StorageT = {
      id: storageID,
      name: `Storage ${storageID}`,
      size: size,
      canDelete: false,
    };
    return [storage];
  });

  return (
    <div
      className={css({
        backgroundColor: 'white',
        padding: '22px 0',
      })}
    >
      <div
        className={css({
          display: 'flex',
          gap: '16px',
        })}
      >
        <div>
          <Label>Platform</Label>
          <Select
            options={providers}
            value={[{ id: cloudProvider }]}
            onChange={(params) => {
              setCloudProvider(params.value[0].id as string);
            }}
            clearable={false}
            overrides={createSelectOverrides('160px')}
          />
        </div>
        {cloud_provider === 'BYOC' ? null : (
          <div>
            <Label>Region</Label>
            <Select
              options={regions}
              value={[{ id: region }]}
              onChange={(params) => {
                setRegion(params.value[0].id as string);
              }}
              clearable={false}
              overrides={createSelectOverrides('280px', '480px')}
            />
          </div>
        )}
      </div>
      <Workspaces
        workspaces={workspaces}
        setWorkspaces={setWorkspaces}
        platform={platform}
        region={region}
        cloud_provider={cloud_provider}
      />
      {cloud_provider === 'BYOC' ? null : (
        <Storages storages={storages} setStorages={setStorages} platform={platform} region={region} />
      )}
      <Footer
        workspaces={workspaces}
        storages={storages}
        platform={platform}
        region={region}
        cloud_provider={cloud_provider}
      />
    </div>
  );
}

function Workspaces({
  workspaces,
  setWorkspaces,
  platform,
  region,
  cloud_provider,
}: {
  workspaces: WorkspaceT[];
  setWorkspaces: Dispatch<SetStateAction<WorkspaceT[]>>;
  platform: string;
  region: string;
  cloud_provider: 'BYOC' | 'TGCloud';
}) {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        paddingTop: '12px',
        marginBottom: '24px',
      })}
    >
      <Section>
        <Title>Workspace</Title>
        <Button
          kind="secondary"
          size="compact"
          startEnhancer={<Plus />}
          onClick={() => {
            const id = ++workspaceID;
            const workspace: WorkspaceT = {
              id: id,
              name: `Workspace ${id}`,
              is_rw: true,
              enable_ha: false,
              enable_insights: false,
              workspace_type: workspaceSizes[0].typeName,
              hours: hours,
              days: days,
              canDelete: true,
            };
            setWorkspaces([...workspaces, workspace]);
          }}
        >
          Add workspace
        </Button>
      </Section>
      <TableContainer>
        <table
          className={css({
            textAlign: 'left',
            width: '100%',
          })}
        >
          <thead>
            <tr
              className={css({
                borderBottom: `2px solid ${theme.colors.divider}`,
              })}
            >
              <Th
                className={css({
                  width: '200px',
                })}
              >
                Workspace
              </Th>
              <Th
                className={css({
                  width: '150px',
                })}
              >
                Type
              </Th>
              <Th>Workspace Size</Th>
              <Th>H/A</Th>
              <Th>Add-Ons</Th>
              <Th>Hours/Day</Th>
              <Th>Days/Month</Th>
              <td width={40}></td>
            </tr>
          </thead>
          <tbody>
            {workspaces.map((ws) => (
              <Workspace
                key={ws.id}
                workspace={ws}
                onChanged={(ws) => {
                  setWorkspaces(workspaces.map((w) => (w.id === ws.id ? ws : w)));
                }}
                onDelete={(ws) => {
                  setWorkspaces(workspaces.filter((w) => w.id !== ws.id));
                }}
                platform={platform}
                region={region}
                cloud_provider={cloud_provider}
              />
            ))}
          </tbody>
        </table>
      </TableContainer>
    </div>
  );
}

function Workspace({
  workspace,
  onChanged,
  onDelete,
  platform,
  region,
  cloud_provider,
}: {
  workspace: WorkspaceT;
  onChanged: (w: WorkspaceT) => void;
  onDelete: (w: WorkspaceT) => void;
  platform: string;
  region: string;
  cloud_provider: 'BYOC' | 'TGCloud';
}) {
  const [css, theme] = useStyletron();

  const totalPrice = calculateWorkspacePrice(pricing, platform, region, workspace, cloud_provider);

  return (
    <>
      <tr className={`group hover:bg-[#F4F4F4]`}>
        <Td>
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            })}
          >
            <SpaceIcon width={20} height={20} color={theme.colors['text.primary']} />
            <EditText text={workspace.name} onChange={(name) => onChanged({ ...workspace, name })} />
          </div>
        </Td>
        <Td>
          <Select
            options={typeOptions}
            clearable={false}
            overrides={createSelectOverrides('130px')}
            value={[
              {
                id: workspace.is_rw ? 'write' : 'read',
              },
            ]}
            onChange={(params) => {
              const is_rw = params.value[0].id === 'write';
              onChanged({
                ...workspace,
                is_rw,
                // reset to ha false when read only
                enable_ha: is_rw ? workspace.enable_ha : false,
              });
            }}
          />
        </Td>
        <Td>
          <Select
            options={workspaceSizes.map((s) => ({
              ...s,
              id: s.typeName,
            }))}
            clearable={false}
            overrides={createSelectOverrides('110px')}
            labelKey="typeName"
            value={[
              {
                id: workspace.workspace_type,
              },
            ]}
            onChange={(params) => {
              onChanged({
                ...workspace,
                workspace_type: params.value[0].id as string,
              });
            }}
          />
        </Td>
        <Td>
          <Select
            options={haOptions}
            clearable={false}
            disabled={!workspace.is_rw}
            getValueLabel={(param) => param.option.id}
            overrides={createSelectOverrides('85px')}
            value={[
              {
                id: workspace.enable_ha ? 'Yes' : 'No',
              },
            ]}
            onChange={(params) => {
              onChanged({
                ...workspace,
                enable_ha: params.value[0].id === 'Yes',
              });
            }}
          />
        </Td>
        <Td>
          <Select
            options={addOnsOptions}
            clearable={false}
            multi={true}
            placeholder=""
            overrides={createSelectOverrides('140px')}
            value={
              workspace.enable_insights
                ? [
                    {
                      id: 'Insights',
                    },
                  ]
                : []
            }
            onChange={(params) => {
              onChanged({
                ...workspace,
                enable_insights: params.value[0]?.id === 'Insights' ? true : false,
              });
            }}
          />
        </Td>
        <Td>
          <Input
            value={workspace.hours}
            onChange={(e) => {
              onChanged({
                ...workspace,
                hours: parseInt(e.currentTarget.value),
              });
            }}
            type="number"
            max={24}
            min={0}
          />
        </Td>
        <Td>
          <Input
            value={workspace.days}
            onChange={(e) => {
              onChanged({
                ...workspace,
                days: parseInt(e.currentTarget.value),
              });
            }}
            type="number"
            max={31}
            min={0}
          />
        </Td>
        <Td width={40}>
          {workspace.canDelete && (
            <div className="invisible group-hover:visible">
              <Button size="compact" shape="square" kind="text" onClick={() => onDelete(workspace)}>
                <Trash2 size={20} color={theme.colors['icon.danger']} />
              </Button>
            </div>
          )}
        </Td>
      </tr>
      <tr className={`group border-b border-b-[${theme.colors.divider}] last:border-b-0`}>
        <td className="text-right pb-4 pr-[48px] group-last:pb-0" colSpan={7}>
          <span
            className={css({
              color: theme.colors['text.tertiary'],
            })}
          >
            Cost/month:
          </span>{' '}
          ${totalPrice.toFixed(2)}
        </td>
      </tr>
    </>
  );
}

function Storages({
  storages,
  setStorages,
  platform,
  region,
}: {
  storages: StorageT[];
  setStorages: Dispatch<SetStateAction<StorageT[]>>;
  platform: string;
  region: string;
}) {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        marginBottom: '24px',
      })}
    >
      <Section>
        <Title>Storage</Title>
        <Button
          kind="secondary"
          size="compact"
          startEnhancer={<Plus />}
          onClick={() => {
            const id = ++storageID;
            const storage: StorageT = {
              id: id,
              name: `Storage ${storageID}`,
              size: size,
              canDelete: true,
            };
            setStorages([...storages, storage]);
          }}
        >
          Add Storage
        </Button>
      </Section>
      <TableContainer
        className={css({
          paddingBottom: '0 !important',
        })}
      >
        <table
          className={css({
            textAlign: 'left',
            width: '100%',
          })}
        >
          <thead>
            <tr
              className={css({
                borderBottom: `2px solid ${theme.colors.divider}`,
              })}
            >
              <Th
                className={css({
                  width: '200px',
                })}
              >
                Storage
              </Th>
              <Th
                className={css({
                  width: '150px',
                })}
              >
                Data Size (GB)
              </Th>
              <td></td>
              <td width={40}></td>
            </tr>
          </thead>
          <tbody>
            {storages.map((storage) => (
              <Storage
                key={storage.id}
                storage={storage}
                onChanged={(storage) => {
                  setStorages(storages.map((s) => (s.id === storage.id ? storage : s)));
                }}
                onDelete={(storage) => {
                  setStorages(storages.filter((s) => s.id !== storage.id));
                }}
                platform={platform}
                region={region}
              />
            ))}
          </tbody>
        </table>
      </TableContainer>
    </div>
  );
}

function Storage({
  storage,
  onChanged,
  onDelete,
  platform,
  region,
}: {
  storage: StorageT;
  onChanged: (w: StorageT) => void;
  onDelete: (w: StorageT) => void;
  platform: string;
  region: string;
}) {
  const [css, theme] = useStyletron();

  const totalPrice = calculateStoragePrice(pricing, platform, region, storage);

  return (
    <>
      <tr className={`group border-b border-b-[${theme.colors.divider}] last:border-b-0 hover:bg-[#F4F4F4]`}>
        <Td>
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            })}
          >
            <StorageIcon />
            <EditText text={storage.name} onChange={(name) => onChanged({ ...storage, name })} />
          </div>
        </Td>
        <Td>
          <Input
            value={storage.size}
            onChange={(e) => {
              onChanged({
                ...storage,
                size: parseInt(e.currentTarget.value),
              });
            }}
            type="number"
            min={0}
          />
        </Td>
        <Td className="text-right">
          <span
            className={css({
              color: theme.colors['text.tertiary'],
            })}
          >
            Cost/month:{' '}
          </span>
          ${totalPrice.toFixed(3)}
        </Td>
        <Td width={40}>
          {storage.canDelete && (
            <div className="invisible group-hover:visible">
              <Button size="compact" shape="square" kind="text" onClick={() => onDelete(storage)}>
                <Trash2 size={20} color={theme.colors['icon.danger']} />
              </Button>
            </div>
          )}
        </Td>
      </tr>
    </>
  );
}

function Footer({
  workspaces,
  storages,
  platform,
  region,
  cloud_provider,
}: {
  workspaces: WorkspaceT[];
  storages: StorageT[];
  platform: string;
  region: string;
  cloud_provider: 'BYOC' | 'TGCloud';
}) {
  const [css, theme] = useStyletron();

  let workspaceTotalPrice = 0;
  workspaces.forEach((ws) => {
    workspaceTotalPrice += calculateWorkspacePrice(pricing, platform, region, ws, cloud_provider);
  });

  let storageTotalPrice = 0;
  storages.forEach((s) => {
    storageTotalPrice += calculateStoragePrice(pricing, platform, region, s);
  });

  const totalPrice = workspaceTotalPrice + storageTotalPrice;

  const url = getSaleMailLink();

  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      })}
    >
      <div
        className={css({
          display: 'flex',
          gap: '8px',
        })}
      >
        <a
          href={url}
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: theme.colors['button.background.primary'],
            borderRadius: '2px',
            padding: '2px 12px',
            color: theme.colors['button.icon.inverse'],
            fontWeight: 500,
            lineHeight: '28px',
          })}
        >
          Contact Sales
        </a>
        <a
          href="https://savanna.tgcloud.io/"
          target="_blank"
          rel="noreferrer"
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            border: `1px solid ${theme.colors['button.border']}`,
            backgroundColor: theme.colors['button.background.default'],
            borderRadius: '2px',
            padding: '2px 12px',
            color: theme.colors['button.text'],
            fontWeight: 500,
            lineHeight: '28px',
          })}
        >
          Start Free Trial
        </a>
      </div>
      <div
        className={css({
          display: 'grid',
          gap: '8px',
          gridTemplateColumns: '2fr 1fr',
        })}
      >
        <span
          className={css({
            color: theme.colors['text.tertiary'],
            textAlign: 'right',
          })}
        >
          Monthly Compute Cost (Usage):{' '}
        </span>
        <span>${workspaceTotalPrice.toFixed(3)}</span>
        <span
          className={css({
            color: theme.colors['text.tertiary'],
            textAlign: 'right',
          })}
        >
          Monthly Storage Cost (Usage):{' '}
        </span>
        <span>${storageTotalPrice.toFixed(3)} </span>
        <span
          className={css({
            color: theme.colors['text.tertiary'],
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '24px',
            textAlign: 'right',
          })}
        >
          Cost/month:
        </span>
        <span
          className={css({
            fontSize: '18px',
            lineHeight: '24px',
            fontWeight: 700,
          })}
        >
          ${totalPrice.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function EditText({ text, onChange }: { text: string; onChange: (text: string) => void }) {
  const [css, theme] = useStyletron();
  const nameRef = useRef<HTMLSpanElement | null>(null);

  const isHovering = useHover(nameRef);
  const [isEditing, setIsEditing] = useState(false);

  const [value, setValue] = useState(text);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const onSubmit = () => {
    setIsEditing(false);

    // do not allow empty string
    if (value.trim() !== '' && value !== text) {
      onChange(value);
    } else {
      setValue(text);
    }
  };

  return (
    <span
      ref={nameRef}
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
      })}
    >
      {isEditing ? (
        <Input
          autoFocus={true}
          value={value}
          onBlur={() => {
            onSubmit();
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onSubmit();
            }
          }}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
      ) : (
        <>
          <span
            className={css({
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '24px',
              marginRight: '2px',
            })}
          >
            {value}
          </span>
          {isHovering && !isEditing ? (
            <Button
              size="compact"
              kind="text"
              shape="square"
              onClick={() => {
                setIsEditing(true);
              }}
            >
              <Pencil size={20} color={theme.colors['icon.informative']} />
            </Button>
          ) : (
            <span
              className={css({
                width: '20px',
              })}
            />
          )}
        </>
      )}
    </span>
  );
}

export const Label = styled('div', ({ $theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '8px',
  lineHeight: '16px',
}));

export const Title = styled('div', ({ $theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
}));

export const Section = styled('div', ({ $theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
}));

export const TableContainer = styled('div', ({ $theme }) => ({
  background: '#FAFAFA',
  padding: '16px',
}));

export const Th = styled('th', ({ $theme }) => ({
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '16px',
}));

export const Td = styled('td', ({ $theme }) => ({
  padding: '16px 8px',
}));

function createSelectOverrides(width?: string, maxHeight?: string): SelectOverrides {
  const selectOverrides: SelectOverrides = {
    Root: {
      style: {
        width: width || '308px',
      },
    },
    Dropdown: {
      style: {
        maxHeight: maxHeight || '340px',
      },
    },
    Popover: {
      props: {
        placement: 'bottom',
        ignoreBoundary: true,
        popperOptions: {
          modifiers: {
            preventOverflow: {
              enabled: false,
            },
            hide: {
              enabled: false,
            },
          },
        },
      },
    },
  };
  return selectOverrides;
}
